/* eslint-disable no-negated-condition */
import {
	Flex,
} from '@bamboohr/fabric';
import {
	useState,
	useEffect,
} from 'react';

import { getShouldMigrateToNewForm, isNewStateTaxWithholdingForm } from './withholding.domain';
import { FedWithholding } from '../fed-withholding.react';
import { FedWithholding2020 } from '../fed-withholding-2020.react';
import { IowaStateWithholding } from '../iowa-state-withholding.react';
import { StateWithholdingLA } from '../state-withholding-la.react';
import { StateWithholding } from '../state-withholding.react';
import { StateUnemployment } from '../state-unemployment.react';
import { WithholdingBlankState } from '../withholding-blank-state.react';
import { validateFederalTaxData, validateStateTaxData } from '../../utils';

/**
 * @param {import("./types").WithholdingProps} props 
 */
export function Withholding(props) {
	const {
		data,
		hasBlankStateHeader,
		headerHeadlineSize,
		newHirePacket,
		show,
		fedPerms,
		statePerms,
		onStateLocationChange, // ({val: "Work" | "Home"}) => void
		onStateUnemploymentLocationChange,
		isAdminUser,
		isGreenhouse = false,
		isPayrollAdminUser = false,
		canAccessStateUiFields = false,
		renderFederal = true,
		renderState = true,
		renderStateUnemployment = true,
		sectionsFlexGap = 4,
		showWithholdingInformation,
		stateSectionNote = '',
		stateUnemploymentSectionNote = '',
		showStateLocationRequiredAsterisk = false,
		showStateUnemploymentLocationRequiredAsterisk = false,
		usesSectionComponent,
	} = props;

	const {
		federal,
		federal2020,
		has2020W4,
		is2020Release,
		state,
		stateUnemployment,
	} = data;

	const [temporary2020, setTemporary2020] = useState(false);
	
	const [selectedStateLocation, setSelectedStateLocation] = useState(state && state.locations.selected ? state.locations.selected : null);
	
	const [shouldMigrateToNewForm , setShouldMigrateToNewForm] = useState(state ? getShouldMigrateToNewForm(state.locations, selectedStateLocation) : false);

	const defaultShowNewStateForm = state ? isNewStateTaxWithholdingForm(state.locations, state.locations.selected) : false;
	
	const [showNewStateForm, setShowNewStateForm] = useState(defaultShowNewStateForm);
	
	const selectedStateLocationInfo = selectedStateLocation ? state.locations.byId?.[selectedStateLocation] : null;
	
	const stateAbbr = selectedStateLocationInfo?.state;
	
	const stateFormVersionId = showNewStateForm && selectedStateLocationInfo?.newFields?.formVersionId !== null ? selectedStateLocationInfo?.newFields?.formVersionId : '';
	const callbackReset2020Form = () => setTemporary2020(false);
	const resetNewStateForm = () => {
		setSelectedStateLocation(state.locations.selected ?? null);
		if (isNewStateTaxWithholdingForm(state.locations, state.locations.selected) === false) {
			setShowNewStateForm(false);
		}
	};

	// If they cancel a 2020 or newIowa form edit, revert back to pre-2020 Federal Withholding or pre-new iowa form fields.
	useEffect(() => {
		const payInfoTabFooter = document.querySelector('button[data-action="SiteFooterAction:cancel"]');

		if (payInfoTabFooter) {
			payInfoTabFooter.addEventListener('click', callbackReset2020Form);
			payInfoTabFooter.addEventListener('click', resetNewStateForm);
		}

		return () => {
			// Clean up: remove the listener when the React component is unmounted
			if (payInfoTabFooter) {
				payInfoTabFooter.removeEventListener('click', callbackReset2020Form);
				payInfoTabFooter.removeEventListener('click', resetNewStateForm);
			}
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	// Resets the new form when the user changes the work location so that they restart the workflow
	// if they switch back. However, if the work location the user switches to is the same state, then
	// do nothing to preserve user progress.
	useEffect(() => {
		const { Work: workLocation, Home: homeLocation } = state?.locations?.byId ?? {};
		const isSameWorkHomeLocation = 
		typeof workLocation !== 'undefined' &&
		typeof homeLocation !== 'undefined' &&
		workLocation?.state === homeLocation?.state;
		
		// if home and work locations are the same, 
		if (!isSameWorkHomeLocation) {
			if (selectedStateLocation) {
				setShouldMigrateToNewForm(getShouldMigrateToNewForm(state.locations, selectedStateLocation));
				setShowNewStateForm(isNewStateTaxWithholdingForm(state.locations, selectedStateLocation));
			} else {
				setShouldMigrateToNewForm(false);
				setShowNewStateForm(false);
		}
	}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [selectedStateLocation]);

	/** @type {boolean | null} */
	let showFederalWithholding = null;
	/** @type {boolean | null} */
	let showStateWithholding = null;
	/** @type {boolean | null} */
	let showStateUi = null;

	if (showWithholdingInformation) {
		({
			showFederalWithholding,
			showStateWithholding,
			showStateUi,
		} = showWithholdingInformation);
	}

	const showFederal2020 = is2020Release && (has2020W4 || temporary2020);

	/** @type {boolean} */
	const isNhpPreview = newHirePacket && window.previewUser;
	const isEmployerView = !window.SESSION_USER?.employeeId || (window.SESSION_USER?.employeeId && (window.SESSION_USER?.employeeId !== window.currentlyEditingEmployeeId))

	const stateAccessLevel = window.stateTaxDeductionsPermissions;
	const unemploymentAccessLevel = window.unemploymentInsurancePermissions;
	// Access Levels: 0 => No Access; 1 or 2 => View; 3 => Edit;
	const stateEditAccessLevel = stateAccessLevel === 3;
	const stateViewOnlyAccessLevel = stateAccessLevel === 2 || stateAccessLevel === 1;
	const unemploymentEditAccessLevel = unemploymentAccessLevel === 3;
	const unemploymentViewOnlyAccessLevel = unemploymentAccessLevel === 2 || unemploymentAccessLevel === 1;

	if (
		(statePerms !== 0 && !validateStateTaxData(data)) ||
		(fedPerms !== 0 && !validateFederalTaxData(data))
	) {
		return (
			<WithholdingBlankState
				hasHeader={hasBlankStateHeader}
				headerHeadlineSize={headerHeadlineSize}
				usesSectionComponent={usesSectionComponent}
			/>
		);
	}

	if (!show) {
		return (
			<div></div>
		);
	}

	const getFederalWithholdingSection = () => {
		if (renderFederal && fedPerms > 0) {
			if (federal2020 && showFederal2020) {
				return <FedWithholding2020
					headerHeadlineSize={ headerHeadlineSize }
					isEmployerView={isEmployerView}
					showExemptions={ isPayrollAdminUser }
					showWithholding={ showFederalWithholding }
					usesSectionComponent={ usesSectionComponent }
					viewOnly={ (fedPerms === 1) || isNhpPreview }
					{ ...federal2020 }
				/>
			}

			if (federal && !showFederal2020) {
				return <FedWithholding
					editPermission={ fedPerms === 3 }
					handle2020ToggleClick={ () => setTemporary2020(true) }
					headerHeadlineSize={ headerHeadlineSize }
					isAdminUser={ isAdminUser }
					isPayrollAdminUser={ isPayrollAdminUser }
					newHirePacket={ newHirePacket }
					show2020Message={ is2020Release }
					usesSectionComponent={ usesSectionComponent }
					viewOnly={ (fedPerms === 1) || is2020Release }
					{ ...federal }
				/>
			}
		}
		return null
	}

	/**
	 * Handles state location change for Louisiana. This handler helps us avoid drilling setters
	 * into the LA withholdings component, while still respecting the onStateLocationChange prop.
	 */
	const handleStateLocationChange = ({ locationVal = '' }) => {
		setSelectedStateLocation(locationVal);
		onStateLocationChange?.({
			val: locationVal
		});
	};

	const getNewStateForm = () => {
		switch (stateAbbr) {
			case 'LA':
				return <StateWithholdingLA
					editPermission={ stateEditAccessLevel }
					headerHeadlineSize={ headerHeadlineSize }
					isAdminUser={ isAdminUser }
					isPayrollAdminUser={ isPayrollAdminUser }
					newHirePacket={ newHirePacket }
					onLocationChange={ handleStateLocationChange }
					sectionNote={ stateSectionNote }
					selectedLocation={ selectedStateLocation }
					showLocationRequiredAsterisk={ showStateLocationRequiredAsterisk }
					showWithholding={ showStateWithholding }
					stateInfo={ state }
					usesSectionComponent={ usesSectionComponent }
					viewOnly={ stateViewOnlyAccessLevel || isNhpPreview }
				/>;
			case 'IA':
				return <IowaStateWithholding
					accessLevel={ stateAccessLevel }
					editPermission={ stateEditAccessLevel }
					headerHeadlineSize={ headerHeadlineSize }
					isAdminUser={ isAdminUser }
					isPayrollAdminUser={ isPayrollAdminUser }
					newHirePacket={ newHirePacket }
					onLocationChange={ onStateLocationChange }
					sectionNote={ stateSectionNote }
					selectedLocation={ selectedStateLocation }
					setNewIowaStateForm={ setShowNewStateForm }
					setSelectedStateLocation={ setSelectedStateLocation }
					showLocationRequiredAsterisk={ showStateLocationRequiredAsterisk }
					showWithholding={ showStateWithholding }
					usesSectionComponent={ usesSectionComponent }
					viewOnly={ stateViewOnlyAccessLevel || isNhpPreview }
					{ ...state }
			/>
			default:
				return null;
		}
	}

	const getStateWithholdingSection = () => {

		// If the user is in a new hire packet and does not have a selected location, we'll
		// hide the state withholding form altogether. The user won't be able to complete
		// the form since the location field will not be prepopulated with a selected location
		// and it is automatically disabled in new hire packets (per current design decisions).
		const shouldShowInNewHirePacket = !((newHirePacket || isNhpPreview) && state.locations.selected === null)

		/*Checks if Access Level permission is on the page*/
		if (stateAccessLevel !== undefined) {
			if (stateEditAccessLevel || stateViewOnlyAccessLevel) {
				if (renderState && state && shouldShowInNewHirePacket) {
					return <>
						<input
							id="stateFormVersionId"
							name="state[formVersionId]"
							type="hidden"
							value={stateFormVersionId}
						/>
						{showNewStateForm
						? getNewStateForm()
						: <StateWithholding
							accessLevel={ stateAccessLevel }
							editPermission={ stateEditAccessLevel }
							handleFormMigrationClick={ () => setShowNewStateForm(true) }
							headerHeadlineSize={ headerHeadlineSize }
							isAdminUser={ isAdminUser }
							isGreenhouse={ isGreenhouse }
							isPayrollAdminUser={ isPayrollAdminUser }
							newHirePacket={ newHirePacket }
							onLocationChange={ onStateLocationChange }
							sectionNote={ stateSectionNote }
							selectedLocation={ selectedStateLocation }
							setNewIowaStateForm={ setShowNewStateForm }
							setSelectedStateLocation={ setSelectedStateLocation }
							shouldMigrateToNewForm={ shouldMigrateToNewForm }
							showLocationRequiredAsterisk={ showStateLocationRequiredAsterisk }
							showWithholding={ showStateWithholding }
							usesSectionComponent={ usesSectionComponent }
							viewOnly={ stateViewOnlyAccessLevel || isNhpPreview }
							{ ...state }
						/>}
					</>
				}
			}
		} else if (renderState && statePerms > 0 && state) {
			return <>
				<input
					id="stateFormVersionId"
					name="state[formVersionId]"
					type="hidden"
					value={stateFormVersionId}
				/>
				{showNewStateForm
				? getNewStateForm()
				: <StateWithholding
					editPermission={ statePerms === 3 }
					handleFormMigrationClick={ () => setShowNewStateForm(true) }
					headerHeadlineSize={ headerHeadlineSize }
					isAdminUser={ isAdminUser }
					isGreenhouse={ isGreenhouse }
					isPayrollAdminUser={ isPayrollAdminUser }
					newHirePacket={ newHirePacket }
					onLocationChange={ onStateLocationChange }
					sectionNote={ stateSectionNote }
					selectedLocation={ selectedStateLocation }
					setNewIowaStateForm={ setShowNewStateForm }
					setSelectedStateLocation={ setSelectedStateLocation }
					shouldMigrateToNewForm={ shouldMigrateToNewForm }
					showLocationRequiredAsterisk={ showStateLocationRequiredAsterisk }
					showWithholding={ showStateWithholding }
					usesSectionComponent={ usesSectionComponent }
					viewOnly={ (statePerms === 1) || isNhpPreview }
					{ ...state }
				/>}
			</>
		}
		// None of the above cases applied
		return null;
	}

	const getUnemploymentSection = () => {
		/*Checks if Access Level permission is on the page*/
		if (unemploymentAccessLevel !== undefined) {
			if ((unemploymentEditAccessLevel || unemploymentViewOnlyAccessLevel)) {
				return <StateUnemployment
					accessLevel={ unemploymentAccessLevel }
					editPermission={ unemploymentEditAccessLevel }
					headerHeadlineSize={ headerHeadlineSize }
					isAdminUser={ isAdminUser }
					isPayrollAdminUser={ isPayrollAdminUser }
					onLocationChange={ onStateUnemploymentLocationChange }
					sectionNote={ stateUnemploymentSectionNote }
					showLocationRequiredAsterisk={ showStateUnemploymentLocationRequiredAsterisk }
					showUnemployment={ showStateUi }
					usesSectionComponent={ usesSectionComponent }
					viewOnly={ unemploymentViewOnlyAccessLevel }
					{ ...stateUnemployment }
				/>
			}
		} else if (renderStateUnemployment && (isAdminUser || isPayrollAdminUser || canAccessStateUiFields) && stateUnemployment) {
			return <StateUnemployment
				editPermission={ isPayrollAdminUser ||  isAdminUser}
				headerHeadlineSize={ headerHeadlineSize }
				isAdminUser={ isAdminUser }
				isPayrollAdminUser={ isPayrollAdminUser }
				onLocationChange={ onStateUnemploymentLocationChange }
				sectionNote={ stateUnemploymentSectionNote }
				showLocationRequiredAsterisk={ showStateUnemploymentLocationRequiredAsterisk }
				showUnemployment={ showStateUi }
				usesSectionComponent={ usesSectionComponent }
				viewOnly={ statePerms === 1 }
				{ ...stateUnemployment }
			/>
		}
		
		return null;
	}

	function renderWithholding() {
		return (
			<>
				{ getFederalWithholdingSection() }
				{ getStateWithholdingSection() }
				{ getUnemploymentSection() }
			</>
		);
	}

	return <Flex flexDirection="column" gap={ sectionsFlexGap }>
		{ renderWithholding() }
	</Flex>
}
